import { useState, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Upload, Download, Image, RotateCcw, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BACKGROUNDS = [
  { id: 'wooden_table',  name: 'Wooden Table',  url: 'https://images.unsplash.com/photo-1690983321303-f510e0575644?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwd29vZGVuJTIwdGFibGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NTQwODU5Nnww&ixlib=rb-4.1.0&q=85' },
  { id: 'cloudy_glass',  name: 'Cloudy Glass',  url: 'https://images.unsplash.com/photo-1631435628939-467169ba6e08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxmcm9zdGVkJTIwZ2xhc3MlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NTQwODYwM3ww&ixlib=rb-4.1.0&q=85' },
  { id: 'black_marble',  name: 'Black Marble',  url: 'https://images.unsplash.com/photo-1650804068570-7fb2e3dbf888?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxibGFjayUyMG1hcmJsZSUyMHRleHR1cmV8ZW58MHx8fHwxNzc1NDA4NjAwfDA&ixlib=rb-4.1.0&q=85' },
  { id: 'white_marble',  name: 'White Marble',  url: 'https://images.unsplash.com/photo-1694378061101-bf38a2fcc596?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMG1hcmJsZSUyMHRleHR1cmUlMjB2ZWluc3xlbnwwfHx8fDE3NzU0MDg1OTF8MA&ixlib=rb-4.1.0&q=85' },
  { id: 'dark_leather',  name: 'Dark Leather',  url: 'https://images.unsplash.com/photo-1764065339829-b2bba9ca509c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbGVhdGhlciUyMHRleHR1cmV8ZW58MHx8fHwxNzc1NDA4NTk2fDA&ixlib=rb-4.1.0&q=85' },
];

// Each preset simulates a camera distance via card scale + shadow depth.
// Shadows are the primary depth cue — multi-layer to mimic real table photography:
//   layer 1: contact shadow  (tight, dark)   → card is physically ON the surface
//   layer 2: lift shadow     (medium)         → card has slight elevation / thickness
//   layer 3: ambient         (huge blur, low) → soft real-world light falloff
//   layer 4: inset highlight (top edge)       → overhead light catches card face
const DISTANCE_PRESETS = {
  '6in': {
    label: '6"',
    cardWidth: '72%',
    minHeight: '400px',
    translateY: '4%',
    shadow: [
      '0 2px 4px rgba(0,0,0,0.45)',
      '0 8px 16px rgba(0,0,0,0.38)',
      '0 22px 44px rgba(0,0,0,0.22)',
      '0 50px 80px rgba(0,0,0,0.10)',
      'inset 0 1px 0 rgba(255,255,255,0.22)',
    ].join(', '),
  },
  '8in': {
    label: '8"',
    cardWidth: '58%',
    minHeight: '460px',
    translateY: '5%',
    shadow: [
      '0 2px 3px rgba(0,0,0,0.35)',
      '0 10px 20px rgba(0,0,0,0.30)',
      '0 30px 60px rgba(0,0,0,0.18)',
      '0 60px 100px rgba(0,0,0,0.08)',
      'inset 0 1px 0 rgba(255,255,255,0.18)',
    ].join(', '),
  },
  '12in': {
    label: '12"',
    cardWidth: '44%',
    minHeight: '520px',
    translateY: '6%',
    shadow: [
      '0 1px 2px rgba(0,0,0,0.25)',
      '0 8px 16px rgba(0,0,0,0.22)',
      '0 30px 60px rgba(0,0,0,0.14)',
      '0 70px 120px rgba(0,0,0,0.07)',
      'inset 0 1px 0 rgba(255,255,255,0.14)',
    ].join(', '),
  },
};

const CardGenerator = () => {
  const { user, refreshUser } = useAuth();
  const [frontImage, setFrontImage]   = useState(null);
  const [backImage, setBackImage]     = useState(null);
  const [selectedBg, setSelectedBg]   = useState(BACKGROUNDS[0]);
  const [rendering, setRendering]     = useState(false);
  const [showFront, setShowFront]     = useState(true);
  const [distance, setDistance]       = useState('8in');   // camera distance preset

  // ONE ref — attached directly to the visible preview container.
  // html2canvas captures exactly this node → WYSIWYG guaranteed.
  const previewRef    = useRef(null);
  const frontInputRef = useRef(null);
  const backInputRef  = useRef(null);

  const hasAccess =
    user?.role === 'admin' ||
    (user?.unlimited_access_until && new Date(user.unlimited_access_until) > new Date()) ||
    (user?.tokens > 0);

  // ─── Upload ────────────────────────────────────────────────────────────────
  const handleImageUpload = (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      if (side === 'front') setFrontImage(event.target.result);
      else                  setBackImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // ─── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setFrontImage(null);
    setBackImage(null);
    if (frontInputRef.current) frontInputRef.current.value = '';
    if (backInputRef.current)  backInputRef.current.value  = '';
  };

  // ─── Download ──────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!frontImage || !backImage) {
      toast.error('Please upload both front and back images');
      return;
    }

    if (!hasAccess) {
      toast.error('No tokens available. Please deposit to continue.');
      return;
    }

    // Guard: ref must be attached before we proceed
    if (!previewRef.current) {
      toast.error('Preview is not ready. Please try again.');
      return;
    }

    setRendering(true);

    try {
      // 1️⃣  Deduct token via API FIRST
      await axios.post(
        `${API_URL}/api/cards/render`,
        { background_id: selectedBg.id },
        { withCredentials: true }
      );

      // 2️⃣  Capture the LIVE preview node — no hidden clone, no separate layout.
      //     scale:3 → high-resolution output (~3× device pixels).
      //     useCORS:true + allowTaint:false → required for cross-origin images
      //     (Unsplash backgrounds, data-URL uploads already work without CORS).
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
        allowTaint: false,
        logging: false,
      });

      // 3️⃣  Trigger browser download
      const link = document.createElement('a');
      link.download = `cardforge-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Card downloaded successfully!');

      // 4️⃣  Refresh token balance once, after everything succeeds
      await refreshUser();

    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to render card';
      toast.error(message);
    } finally {
      setRendering(false);
    }
  };

  // ─── Shared card-face style ─────────────────────────────────────────────────
  // Using inline style (not className) so html2canvas captures it faithfully.
  const cardFaceStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="space-y-8 animate-fadeIn">

        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Card Generator
          </h1>
          <p className="text-zinc-500 mt-2">
            Upload your images and create stunning card renders
          </p>
        </div>

        {/* Access Warning */}
        {!hasAccess && (
          <div
            className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg"
            data-testid="no-access-warning"
          >
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-amber-400 text-sm">
              You have no tokens. Please{' '}
              <a href="/deposit" className="underline font-semibold">deposit</a>{' '}
              to use the generator.
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">

          {/* ── Upload Section ─────────────────────────────────────────────── */}
          <div className="space-y-6">

            {/* Front Image */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-amber-400" />
                Front of Card
              </h3>
              <div
                onClick={() => frontInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                  frontImage
                    ? 'border-amber-500/50 bg-amber-500/5'
                    : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                }`}
                data-testid="upload-front-zone"
              >
                <input
                  ref={frontInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'front')}
                  className="hidden"
                  data-testid="upload-front-input"
                />
                {frontImage ? (
                  <div className="space-y-3">
                    <Check className="w-8 h-8 text-amber-400 mx-auto" />
                    <p className="text-zinc-400 text-sm">Front image uploaded</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-8 h-8 text-zinc-500 mx-auto" />
                    <p className="text-zinc-400 text-sm">Click to upload front image</p>
                  </div>
                )}
              </div>
            </div>

            {/* Back Image */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-amber-400" />
                Back of Card
              </h3>
              <div
                onClick={() => backInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                  backImage
                    ? 'border-amber-500/50 bg-amber-500/5'
                    : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                }`}
                data-testid="upload-back-zone"
              >
                <input
                  ref={backInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'back')}
                  className="hidden"
                  data-testid="upload-back-input"
                />
                {backImage ? (
                  <div className="space-y-3">
                    <Check className="w-8 h-8 text-amber-400 mx-auto" />
                    <p className="text-zinc-400 text-sm">Back image uploaded</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-8 h-8 text-zinc-500 mx-auto" />
                    <p className="text-zinc-400 text-sm">Click to upload back image</p>
                  </div>
                )}
              </div>
            </div>

            {/* Background Selection */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4">
                Select Background
              </h3>
              <div className="grid grid-cols-5 gap-3">
                {BACKGROUNDS.map((bg) => (
                  <button
                    key={bg.id}
                    onClick={() => setSelectedBg(bg)}
                    data-testid={`bg-${bg.id}`}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      selectedBg.id === bg.id
                        ? 'border-amber-500 ring-2 ring-amber-500/30'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <img
                      src={bg.url}
                      alt={bg.name}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                    />
                  </button>
                ))}
              </div>
              <p className="text-zinc-500 text-sm mt-3 text-center">{selectedBg.name}</p>
            </div>
          </div>

          {/* ── Preview + Actions ───────────────────────────────────────────── */}
          <div className="space-y-6">
            <div className="glass rounded-xl p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4">
                Preview
              </h3>

              {/*
               * ┌─────────────────────────────────────────────────────┐
               * │  THIS is the node html2canvas captures.             │
               * │  previewRef → attached here, not on a hidden clone. │
               * │  Background is a real <img> (not CSS background-    │
               * │  image) so html2canvas captures it cross-origin.    │
               * └─────────────────────────────────────────────────────┘
               */}
              <div
                ref={previewRef}
                className="relative w-full rounded-xl overflow-hidden"
                style={{ minHeight: DISTANCE_PRESETS[distance].minHeight, transition: 'min-height 0.3s ease' }}
                data-testid="card-preview"
              >
                {/* Background — real <img> for reliable html2canvas capture */}
                <img
                  src={selectedBg.url}
                  alt=""
                  aria-hidden="true"
                  crossOrigin="anonymous"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                    pointerEvents: 'none',
                  }}
                />

                {/* Card scene — centres card and applies camera-distance offset */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                    // Push card slightly below-centre so more "table" shows above
                    // (matches how a real table photo is composed)
                    transform: `translateY(${DISTANCE_PRESETS[distance].translateY})`,
                    transition: 'transform 0.3s ease',
                  }}
                >
                  {/*
                   * TWO-WRAPPER: outer rotates + shadows, inner clips.
                   * Keeping them separate prevents border-radius corner bleed.
                   * Using 2D transforms only (scale + rotateZ) — html2canvas
                   * does not support rotateX/perspective, so depth is conveyed
                   * entirely through multi-layer shadow (same technique as real
                   * product/card photography lightboxes).
                   */}

                  {/* OUTER — scale (zoom) + rotateZ (tilt) + shadow. NO clip. */}
                  <div
                    style={{
                      width: DISTANCE_PRESETS[distance].cardWidth,
                      maxWidth: '460px',
                      aspectRatio: '3.375 / 2.125',
                      position: 'relative',
                      transform: `rotateZ(${showFront ? '-2deg' : '2deg'})`,
                      boxShadow: DISTANCE_PRESETS[distance].shadow,
                      transition: 'width 0.3s ease, box-shadow 0.3s ease, transform 0.25s ease',
                    }}
                  >
                    {/* INNER — borderRadius + overflow:hidden clip only. NO transform. */}
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: 'rgba(24,24,27,0.5)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      {showFront ? (
                        frontImage ? (
                          <img src={frontImage} alt="Front" crossOrigin="anonymous" style={cardFaceStyle} />
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                            <p style={{ color: '#71717a', fontSize: '0.875rem' }}>Front</p>
                          </div>
                        )
                      ) : (
                        backImage ? (
                          <img src={backImage} alt="Back" crossOrigin="anonymous" style={cardFaceStyle} />
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                            <p style={{ color: '#71717a', fontSize: '0.875rem' }}>Back</p>
                          </div>
                        )
                      )}
                    </div>
                    {/* end INNER clip wrapper */}
                  </div>
                  {/* end OUTER rotation wrapper */}
                </div>
              </div>
              {/* ── end previewRef ── */}

              {/* Front / Back + Distance toggles */}
              <div className="flex flex-col gap-3 mt-4">
                {/* Front / Back */}
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setShowFront(true)}
                    data-testid="show-front-btn"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      showFront ? 'bg-amber-500 text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                    }`}
                  >
                    Front
                  </button>
                  <button
                    onClick={() => setShowFront(false)}
                    data-testid="show-back-btn"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      !showFront ? 'bg-amber-500 text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                    }`}
                  >
                    Back
                  </button>
                </div>

                {/* Distance selector */}
                <div className="flex justify-center items-center gap-2">
                  <span className="text-zinc-500 text-xs uppercase tracking-wider mr-1">Distance</span>
                  {Object.entries(DISTANCE_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => setDistance(key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        distance === key
                          ? 'bg-white/15 text-white ring-1 ring-white/20'
                          : 'bg-white/5 text-zinc-500 hover:bg-white/10 hover:text-zinc-300'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="flex-1 h-12 border-white/10 text-white hover:bg-white/5"
                  data-testid="reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>

                <Button
                  onClick={handleDownload}
                  disabled={!frontImage || !backImage || !hasAccess || rendering}
                  className="flex-1 h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="download-btn"
                >
                  {rendering ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Rendering…
                    </span>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download (1 Token)
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Token Info */}
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Your Balance</p>
                  <p className="text-2xl font-bold text-amber-400 font-mono" data-testid="card-page-token-balance">
                    {user?.tokens?.toLocaleString() || 0} tokens
                  </p>
                </div>
                {user?.unlimited_access_until && new Date(user.unlimited_access_until) > new Date() && (
                  <div className="text-right">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Unlimited Until</p>
                    <p className="text-lg font-semibold text-emerald-400">
                      {new Date(user.unlimited_access_until).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CardGenerator;
